import { useAppDispatch, useAppSelector, uiActions } from '../../store/index'
import { useNavigate, useLocation } from 'react-router-dom'
import type { UserRole } from '../../types/index'

const isOrganizerRole = (role: UserRole | null) => role === 'system_admin' || role === 'race_organizer'

export function NavBar() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const userRole = useAppSelector(state => state.ui.userRole)

  const isSystemAdmin = userRole === 'system_admin'
  const isRaceOrganizer = userRole === 'race_organizer'
  const isSpectator = userRole === 'spectator'
  const isRunner = userRole === 'runner'

  const activeTab = (() => {
    const path = location.pathname
    if (path.startsWith('/sysadmin')) return 'sysadmin'
    if (path.startsWith('/org-map')) return 'map'
    if (path.startsWith('/org-log')) return 'org-log'
    if (path.startsWith('/org')) return 'org'
    if (path.startsWith('/admin')) return 'admin'
    if (path.startsWith('/log')) return 'log'
    if (path.startsWith('/profile')) return 'profile'
    if (path.startsWith('/team')) return 'team'
    return 'map'
  })()

  const handleTabChange = (tab: string, path?: string) => {
    if (tab !== 'profile' && tab !== 'org' && tab !== 'org-log') {
      dispatch(uiActions.setActiveTab(tab as any))
    }
    navigate(path ?? `/${tab}`)
  }

  // ── System admin: all org tabs + system admin tab ─────────────────────────
  if (isSystemAdmin) {
    const saTabs = [
      {
        id: 'map', label: 'Map', path: '/org-map',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
      },
      {
        id: 'org-log', label: 'Race Log', path: '/org-log',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
      },
      {
        id: 'org', label: 'Organizer', path: '/org',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>,
      },
      {
        id: 'sysadmin', label: 'System', path: '/sysadmin',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      },
    ]
    return (
      <nav className="fixed bottom-0 left-0 right-0 z-[1000] bg-gray-900 text-white border-t border-gray-700 safe-bottom">
        <div className="flex">
          {saTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id, tab.path)}
              className={`flex-1 py-2.5 px-1 flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === tab.id ? 'bg-blue-600 border-t-2 border-blue-400' : 'hover:bg-gray-800'
              }`}
            >
              {tab.icon}
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    )
  }

  // ── Race organizer: Map, Org Log, Organizer only ───────────────────────────
  if (isRaceOrganizer) {
    const orgTabs = [
      {
        id: 'map', label: 'Map', path: '/org-map',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
      },
      {
        id: 'org-log', label: 'Race Log', path: '/org-log',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
      },
      {
        id: 'org', label: 'Organizer', path: '/org',
        icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>,
      },
    ]
    return (
      <nav className="fixed bottom-0 left-0 right-0 z-[1000] bg-gray-900 text-white border-t border-gray-700 safe-bottom">
        <div className="flex">
          {orgTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id, tab.path)}
              className={`flex-1 py-2.5 px-1 flex flex-col items-center gap-0.5 transition-colors ${
                activeTab === tab.id ? 'bg-blue-600 border-t-2 border-blue-400' : 'hover:bg-gray-800'
              }`}
            >
              {tab.icon}
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    )
  }

  // ── All other roles ────────────────────────────────────────────────────────
  const allTabs = [
    {
      id: 'map', label: 'Map', spectatorAllowed: true,
      icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>,
    },
    {
      id: 'team', label: 'Team', spectatorAllowed: true,
      icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    },
    ...(isRunner ? [{
      id: 'profile', label: 'Profile', spectatorAllowed: false,
      icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    }] : []),
    {
      id: 'log', label: 'Log', spectatorAllowed: false,
      icon: <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
    },
  ]

  const tabs = isSpectator ? allTabs.filter(t => t.spectatorAllowed) : allTabs

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[1000] bg-gray-900 text-white border-t border-gray-700 safe-bottom">
      <div className="flex">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id, tab.id === 'org' ? '/org' : undefined)}
            className={`flex-1 py-2.5 px-1 flex flex-col items-center gap-0.5 transition-colors ${
              activeTab === tab.id ? 'bg-blue-600 border-t-2 border-blue-400' : 'hover:bg-gray-800'
            }`}
          >
            {tab.icon}
            <span className="text-xs">{tab.label}</span>
          </button>
        ))}
      </div>
    </nav>
  )
}
